import React, { useEffect, useState } from 'react';
import { getCurrentUser, fetchUserAttributes } from '@aws-amplify/auth';
import { Button, Form, Spinner } from 'react-bootstrap';
import ResetPassword from '../components/ResetPassword';
import styles from './Account.module.css';
import { Theme, getStoredThemePreference, setStoredThemePreference, applyThemeBasedOnAuth } from '../utils/theme';
import { getSubscription, SubscriptionData, getMyOrg, getOrgUsers, MyOrgData, OrganizationUser } from '../utils/subscription';
import CreateTeamModal from '../components/CreateTeamModal';
import AddTeamMemberModal from '../components/AddTeamMemberModal';
import EditTeamNameModal from '../components/EditTeamNameModal';

interface UserInfo {
  email: string;
  userId: string;
}

const Account: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<Theme>(getStoredThemePreference());
  const [videoQuality, setVideoQuality] = useState(() => {
    return localStorage.getItem('video_quality') || '1080p';
  });
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  
  // Team management state
  const [myOrgData, setMyOrgData] = useState<MyOrgData | null>(null);
  const [teamMembers, setTeamMembers] = useState<OrganizationUser[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);
  const [isAddTeamMemberModalOpen, setIsAddTeamMemberModalOpen] = useState(false);
  const [isEditTeamNameModalOpen, setIsEditTeamNameModalOpen] = useState(false);

  const fetchTeamData = async () => {
    try {
      setIsLoadingTeam(true);
      const orgData = await getMyOrg();
      setMyOrgData(orgData);
      
      if (orgData.organization) {
        const users = await getOrgUsers(orgData.organization.org_id);
        setTeamMembers(users);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      setMyOrgData({ organization: null, admin: false });
      setTeamMembers([]);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        setUserInfo({
          email: user.signInDetails?.loginId || '',
          userId: user.username
        });

        // Get subscription data
        const subscription = await getSubscription();
        setSubscriptionData(subscription);
        setHasSubscription(subscription.is_active);
        
        // Fetch team data if user is team admin with active subscription
        if (subscription.is_active && subscription.variant_name === 'team' && subscription.is_sub_owner) {
          await fetchTeamData();
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  const handleThemeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newTheme = event.target.value as Theme;
    setStoredThemePreference(newTheme);
    setCurrentTheme(newTheme);
    applyThemeBasedOnAuth(true, hasSubscription);
  };

  const handleVideoQualityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newQuality = event.target.value;
    setVideoQuality(newQuality);
    localStorage.setItem('video_quality', newQuality);
  };

  const handleManageSubscription = async () => {
    try {
      setIsLoadingPortal(true);
      window.location.href = 'https://juhakiili.lemonsqueezy.com/billing';
    } catch (error) {
      console.error('Error getting customer portal URL:', error);
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleTeamCreated = async (teamData: { org_id: string; name: string }) => {
    // Refresh team data after creation
    await fetchTeamData();
  };

  const handleRemoveMember = async (userId: string) => {
    if (!myOrgData?.organization?.org_id) {
      alert('Organization not found');
      return;
    }

    if (!userInfo) {
      alert('User information not found');
      return;
    }

    if (userId === userInfo.userId) {
      alert('You cannot remove yourself from the team');
      return;
    }

    if (window.confirm('Are you sure you want to remove this team member?')) {
      try {
        const { removeTeamMember } = await import('../utils/subscription');
        await removeTeamMember(myOrgData.organization.org_id, userId);
        await fetchTeamData(); // Refresh team data
      } catch (error: any) {
        console.error('Error removing team member:', error);
        alert(error.message || 'Failed to remove team member');
      }
    }
  };

  const handleAddMember = () => {
    setIsAddTeamMemberModalOpen(true);
  };

  const handleMemberAdded = async () => {
    // Refresh team data after adding member
    await fetchTeamData();
  };

  const handleTeamNameUpdated = async (newName: string) => {
    // Update the local state with the new team name
    if (myOrgData?.organization) {
      setMyOrgData({
        ...myOrgData,
        organization: {
          ...myOrgData.organization,
          name: newName
        }
      });
    }
  };

  const planLabel = subscriptionData?.is_active
    ? (subscriptionData.variant_name === 'team' ? 'TEAM' : 'PRO')
    : 'FREE';

  // Determine if user should see team management section
  const shouldShowTeamManagement = subscriptionData?.is_active && 
                                   subscriptionData?.variant_name === 'team' && 
                                   subscriptionData?.is_sub_owner;

  if (loading) {
    return (
      <div className={styles.accountContainer}>
        <div className={styles.accountContent}>
          <div className={styles.loadingContainer}>
            <Spinner animation="border" role="status" />
            <span className={styles.loadingText}>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className={styles.accountContainer}>
        <div className={styles.accountContent}>
          <div className={styles.loadingContainer}>
            <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: '1.2rem' }}></i>
            <span className={styles.loadingText}>Error loading user information</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.accountContainer}>
      <div className={styles.accountContent}>
        <h2 className={styles.accountHeader}>
          <i className="bi bi-person me-3"></i>
          Account
        </h2>
        <div className={styles.accountInfo}>
          <div className={styles.infoRow}>
            <div className={styles.infoLabel}>Email</div>
            <div className={styles.infoValue}>{userInfo.email}</div>
          </div>
          <div className={styles.infoRow}>
            <div className={styles.infoLabel}>Password</div>
            <div className={styles.infoValue}>
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowResetPassword(true)}
              >
                Reset Password
              </Button>
            </div>
          </div>
          <div className={styles.infoRow}>
            <div className={styles.infoLabel}>Plan</div>
            <div className={styles.infoValue}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  {planLabel === 'TEAM' ? (
                    <span className={styles.teamTag}>TEAM</span>
                  ) : planLabel === 'PRO' ? (
                    <span className={styles.proTag}>PRO</span>
                  ) : (
                    'FREE'
                  )}
                </div>
              </div>
            </div>
          </div>
          {subscriptionData?.is_active && (
            <div className={styles.infoRow}>
              <div className={styles.infoLabel}>Subscription</div>
              <div className={styles.infoValue}>
                <div className="d-flex justify-content-between align-items-center">
                  <Button 
                    variant="warning" 
                    size="sm"
                    onClick={handleManageSubscription}
                    disabled={isLoadingPortal}
                  >
                    {isLoadingPortal ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </>
                    ) : (
                      'Manage Subscription'
                    )}
                  </Button>
                  <div>
                    {subscriptionData.subscription_status === 'active' && subscriptionData.renews_at && (
                      <span className={styles.subscriptionDate}>
                        Next Payment: {subscriptionData.renews_at.substring(0, 10)}
                      </span>
                    )}
                    {subscriptionData.subscription_status !== 'active' && subscriptionData.ends_at && (
                      <span className={styles.subscriptionDate}>
                        Subscription cancelled, expiring: {subscriptionData.ends_at.substring(0, 10)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <h2 className={styles.settingsHeader}>
          <i className="bi bi-gear me-3"></i>
          Settings
        </h2>
        <div className={styles.accountInfo}>
          <div className={styles.infoRow}>
            <div className={styles.infoLabel}>Theme</div>
            <div className={styles.infoValue}>
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    value={currentTheme}
                    onChange={handleThemeChange}
                    disabled={!hasSubscription && currentTheme === 'light'}
                    style={{ minWidth: '120px' }}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </Form.Select>
                  {!hasSubscription && currentTheme === 'light' && (
                    <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                      Dark theme is unavailable for this account
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoLabel}>Video Quality</div>
            <div className={styles.infoValue}>
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    value={videoQuality}
                    onChange={handleVideoQualityChange}
                    style={{ minWidth: '120px' }}
                  >
                    <option value="720p">720p</option>
                    <option value="1080p">1080p</option>
                    <option value="1440p" disabled={!hasSubscription}>1440p {!hasSubscription && '(PRO)'}</option>
                    <option value="4k" disabled={!hasSubscription}>4K {!hasSubscription && '(PRO)'}</option>
                  </Form.Select>
                  {!hasSubscription && (
                    <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                      1440p and 4K are unavailable for this account
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {shouldShowTeamManagement && (
          <div className={styles.teamManagementContainer}>
            <h2 className={styles.settingsHeader}>
              <i className="bi bi-people me-3"></i>
              Team Management
            </h2>
            <div className={styles.accountInfo}>
              {isLoadingTeam ? (
                <div className={styles.teamLoadingSpinner}>
                  <Spinner animation="border" size="sm" />
                  <span>Loading team data...</span>
                </div>
              ) : myOrgData?.organization ? (
                <div className={styles.teamManagementSection}>
                  <div className={styles.infoRow}>
                    <div className={styles.infoLabel}>Team Name</div>
                    <div className={styles.infoValue}>
                      <div className={styles.teamNameContainer}>
                        <span 
                          className={styles.teamName}
                          onClick={() => myOrgData.admin && setIsEditTeamNameModalOpen(true)}
                          title={myOrgData.admin ? "Click to edit team name" : undefined}
                        >
                          {myOrgData.organization.name}
                        </span>
                        {myOrgData.admin && (
                          <button
                            className={styles.editTeamNameButton}
                            onClick={() => setIsEditTeamNameModalOpen(true)}
                            title="Edit team name"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {teamMembers.length > 0 ? (
                    <>
                      <table className={styles.teamMembersTable}>
                        {/* <thead>
                          <tr>
                            <th>Member</th>
                            <th>Role</th>
                            <th>Actions</th>
                          </tr>
                        </thead> */}
                        <tbody>
                          {teamMembers.map((member) => (
                            <tr key={member.user_id} className={styles.teamMemberRow}>
                              <td>
                                <span className={styles.teamMemberNickname}>
                                  {member.user_id === userInfo.userId ? 'You' : (member.nickname || member.user_id)}
                                </span>
                              </td>
                              <td>
                                <span className={styles.teamMemberEmail}>
                                  {member.email || '—'}
                                </span>
                              </td>
                              <td>
                                {member.admin ? 'Admin' : 'Member'}
                              </td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className={styles.removeButton}
                                  onClick={() => handleRemoveMember(member.user_id)}
                                  disabled={member.user_id === userInfo.userId}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className={styles.addMemberButton}>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleAddMember}
                        >
                          <i className="bi bi-person-plus me-2"></i>
                          Add Team Member
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div>No team members found</div>
                  )}
                </div>
              ) : (
                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>Team</div>
                  <div className={styles.infoValue}>
                    <Button
                      variant="primary"
                      onClick={() => setIsCreateTeamModalOpen(true)}
                    >
                      Create Team
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <h2 className={styles.settingsHeader}>
          <i className="bi bi-chat-quote me-3"></i>
          Support
        </h2>
        <div className={styles.accountInfo}>
          <div className={styles.infoRow}>
            <div className={styles.infoLabel}>Need Help?</div>
            <div className={styles.supportValue}>
              <p>We are ready to help you on our Discord channel.</p>
              <a 
                href="https://discord.gg/EQm6ZNMPh5" 
                target="_blank" 
                className={styles.discordButton} 
                rel="noreferrer"
              >
                <img src="/discord-icon.png" height={15} alt="Discord icon" />
                <span className={styles.discordLabel}>Join Discord</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <ResetPassword
        show={showResetPassword}
        onHide={() => setShowResetPassword(false)}
        email={userInfo.email}
      />

      <CreateTeamModal
        isOpen={isCreateTeamModalOpen}
        onClose={() => setIsCreateTeamModalOpen(false)}
        onTeamCreated={handleTeamCreated}
      />

      <AddTeamMemberModal
        isOpen={isAddTeamMemberModalOpen}
        onClose={() => setIsAddTeamMemberModalOpen(false)}
        onMemberAdded={handleMemberAdded}
        orgId={myOrgData?.organization?.org_id || ''}
      />

      <EditTeamNameModal
        isOpen={isEditTeamNameModalOpen}
        onClose={() => setIsEditTeamNameModalOpen(false)}
        onTeamNameUpdated={handleTeamNameUpdated}
        currentName={myOrgData?.organization?.name || ''}
        orgId={myOrgData?.organization?.org_id || ''}
      />
    </div>
  );
};

export default Account; 